@file:Suppress("UNCHECKED_CAST_TO_EXTERNAL_INTERFACE")

package org.example.project.engineservice.cr

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import org.example.project.dashboard.DashboardTheme
import org.example.project.engineletter.layouts.CRLayoutSys
import org.example.project.service.ZustandManager
import org.example.project.utils.All
import org.jetbrains.compose.web.attributes.disabled
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

private const val CUS_CR_DETAIL_MAX_WIDTH = 980

@Composable
internal fun CUSCRBearbeitenLayout(
    verlaufCrs: List<All>,
    verlaufFilter: String,
    selectedCrId: String?,
    showPdfPreview: Boolean,
    selectedPreviewLayout: CrPreviewLayout?,
    previewOrtDatumText: String,
    previewSignaturName: String,
    previewSignaturRole: String,
    previewFirmenName: String,
    previewSignaturBild: String,
    onFilterChange: (String) -> Unit,
    onOpenCr: (All) -> Unit,
    onSelectCr: (String?) -> Unit,
    onOpenDocument: (All) -> Unit,
    onOpenChat: (All) -> Unit,
    accepting: Boolean,
    onAcceptOffer: (All) -> Unit,
    confirmingReadyForDeployment: Boolean,
    onConfirmReadyForDeployment: (All) -> Unit,
    onShowPdfPreviewChange: (Boolean) -> Unit
) {
    val selectedCr = verlaufCrs.firstOrNull { it.id == selectedCrId }
    val offeneTickets = verlaufCrs.count { !ZustandManager.CR.isFinal(it.zustand) }
    val abgeschlosseneTickets = verlaufCrs.count { ZustandManager.CR.isFinal(it.zustand) }
    val filteredCrs = when (verlaufFilter) {
        "OFFEN" -> verlaufCrs.filter { !ZustandManager.CR.isFinal(it.zustand) }
        "CLOSE" -> verlaufCrs.filter { ZustandManager.CR.isFinal(it.zustand) }
        else -> verlaufCrs
    }

    Div({
        style {
            width(100.percent)
            flexGrow(1)
            minHeight(0.px)
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            gap(10.px)
            property("overflow", "hidden")
        }
    }) {
        if (selectedCr == null) {
            CUSCRBearbeitenList(
                verlaufCrs = verlaufCrs,
                verlaufFilter = verlaufFilter,
                filteredCrs = filteredCrs,
                offeneTickets = offeneTickets,
                abgeschlosseneTickets = abgeschlosseneTickets,
                onFilterChange = onFilterChange,
                onOpenCr = onOpenCr
            )
        } else {
            CUSCRBearbeitenDetail(
                selectedCr = selectedCr,
                onBackToList = { onSelectCr(null) },
                onOpenDocument = { onOpenDocument(selectedCr) },
                onOpenChat = { onOpenChat(selectedCr) },
                accepting = accepting,
                onAcceptOffer = { onAcceptOffer(selectedCr) },
                confirmingReadyForDeployment = confirmingReadyForDeployment,
                onConfirmReadyForDeployment = { onConfirmReadyForDeployment(selectedCr) }
            )
        }
    }

    if (showPdfPreview) {
        CUSCRBearbeitenPreviewOverlay(
            selectedPreviewLayout = selectedPreviewLayout,
            previewOrtDatumText = previewOrtDatumText,
            previewSignaturName = previewSignaturName,
            previewSignaturRole = previewSignaturRole,
            previewFirmenName = previewFirmenName,
            previewSignaturBild = previewSignaturBild,
            onShowPdfPreviewChange = onShowPdfPreviewChange
        )
    }
}

@Composable
private fun CUSCRBearbeitenList(
    verlaufCrs: List<All>,
    verlaufFilter: String,
    filteredCrs: List<All>,
    offeneTickets: Int,
    abgeschlosseneTickets: Int,
    onFilterChange: (String) -> Unit,
    onOpenCr: (All) -> Unit
) {
    Div({
        style {
            width(100.percent)
            display(DisplayStyle.Flex)
            justifyContent(JustifyContent.Center)
            alignItems(AlignItems.Center)
            gap(14.px)
            paddingBottom(16.px)
            property("flex-wrap", "wrap")
        }
    }) {
        @Composable
        fun counterItem(value: Int, filterKey: String, tint: String, numberColor: String) {
            Div({
                style {
                    width(156.px)
                    height(72.px)
                    padding(8.px, 12.px)
                    borderRadius(8.px)
                    backgroundColor(DashboardTheme.SURFACE_CARD)
                    property(
                        "border",
                        if (verlaufFilter == filterKey) "1px solid ${DashboardTheme.ACCENT}" else "1px solid ${DashboardTheme.BORDER_LIGHT}"
                    )
                    display(DisplayStyle.Grid)
                    gridTemplateColumns("54px minmax(0, 1fr)")
                    alignItems(AlignItems.Center)
                    gap(8.px)
                    cursor("pointer")
                    property("box-sizing", "border-box")
                }
                onClick { onFilterChange(filterKey) }
            }) {
                Img(src = "images/cr.svg") {
                    style {
                        width(42.px)
                        height(42.px)
                        property("filter", tint)
                    }
                }
                Span({
                    style {
                        fontSize(24.px)
                        fontWeight("750")
                        textAlign("center")
                        color(Color(numberColor))
                        property("color", numberColor)
                    }
                }) { Text(value.toString()) }
            }
        }

        Div({
            style {
                width(54.px)
                height(72.px)
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.Center)
                alignItems(AlignItems.Center)
            }
        }) {
            Img(src = "images/filter_list_off.svg") {
                style {
                    width(28.px)
                    height(28.px)
                    property("filter", "brightness(0) invert(1)")
                }
            }
        }
        counterItem(verlaufCrs.size, "ALLE", "brightness(0) invert(1)", "#ffffff")
        counterItem(offeneTickets, "OFFEN", "brightness(0) saturate(100%) invert(24%) sepia(92%) saturate(2920%) hue-rotate(344deg) brightness(98%) contrast(93%)", "#ff4d4f")
        counterItem(abgeschlosseneTickets, "CLOSE", "brightness(0) saturate(100%) invert(65%) sepia(31%) saturate(690%) hue-rotate(78deg) brightness(94%) contrast(89%)", "#66BB6A")
    }

    Div({
        style {
            flexGrow(1)
            minHeight(0.px)
            marginTop(18.px)
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            gap(12.px)
            alignItems(AlignItems.Center)
            property("overflow-y", "auto")
            property("overflow-x", "hidden")
            property("overscroll-behavior", "contain")
            property("scroll-snap-type", "y mandatory")
            property("scroll-behavior", "smooth")
            paddingRight(4.px)
        }
    }) {
        if (filteredCrs.isEmpty()) {
            Div({
                style {
                    width(100.percent)
                    display(DisplayStyle.Flex)
                    justifyContent(JustifyContent.Center)
                    paddingTop(80.px)
                    color(Color("#8a8f9a"))
                    textAlign("center")
                }
            }) { Text("Keine CR-Tickets vorhanden.") }
        } else {
            filteredCrs.forEach { cr ->
                val statusTint = if (ZustandManager.CR.isFinal(cr.zustand)) {
                    "brightness(0) saturate(100%) invert(65%) sepia(31%) saturate(690%) hue-rotate(78deg) brightness(94%) contrast(89%) drop-shadow(0 0 8px rgba(102,187,106,0.65))"
                } else {
                    "brightness(0) saturate(100%) invert(24%) sepia(92%) saturate(2920%) hue-rotate(344deg) brightness(98%) contrast(93%) drop-shadow(0 0 8px rgba(255,77,79,0.65))"
                }
                Div({
                    style {
                        width(100.percent)
                        display(DisplayStyle.Flex)
                        justifyContent(JustifyContent.Center)
                        property("box-sizing", "border-box")
                    }
                }) {
                    Div({
                        style {
                            width(420.px)
                            height(100.px)
                            padding(10.px)
                            borderRadius(8.px)
                            backgroundColor(DashboardTheme.SURFACE_CARD)
                            property("border", "1px solid ${DashboardTheme.BORDER_LIGHT}")
                            display(DisplayStyle.Grid)
                            gridTemplateColumns("76px minmax(0, 1fr)")
                            alignItems(AlignItems.Center)
                            gap(10.px)
                            cursor("pointer")
                            property("box-sizing", "border-box")
                        }
                        onClick { onOpenCr(cr) }
                    }) {
                        Img(src = "images/crfinish.svg", attrs = {
                            style {
                                width(50.px)
                                height(50.px)
                                property("filter", statusTint)
                            }
                        })
                        Div({
                            style {
                                display(DisplayStyle.Grid)
                                gridTemplateRows("1fr 1fr")
                                alignItems(AlignItems.Center)
                                justifyItems("center")
                                minWidth(0.px)
                                textAlign("center")
                            }
                        }) {
                            Span({
                                style {
                                    width(100.percent)
                                    fontSize(16.px)
                                    fontWeight("700")
                                    property("white-space", "nowrap")
                                    property("overflow", "hidden")
                                    property("text-overflow", "ellipsis")
                                }
                            }) { Text(cr.crNummer.ifBlank { "CR-${cr.id}" }) }
                            Span({
                                style {
                                    width(100.percent)
                                    color(DashboardTheme.TEXT_MUTED)
                                    fontSize(13.px)
                                    fontWeight("600")
                                    property("white-space", "nowrap")
                                    property("overflow", "hidden")
                                    property("text-overflow", "ellipsis")
                                }
                            }) {
                                Text(ZustandManager.CR.textFor(cr.zustand))
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun CUSCRBearbeitenDetail(
    selectedCr: All,
    onBackToList: () -> Unit,
    onOpenDocument: () -> Unit,
    onOpenChat: () -> Unit,
    accepting: Boolean,
    onAcceptOffer: () -> Unit,
    confirmingReadyForDeployment: Boolean,
    onConfirmReadyForDeployment: () -> Unit
) {
    var showAnforderung by remember(selectedCr.id) { mutableStateOf(false) }
    var showCrInhalt by remember(selectedCr.id) { mutableStateOf(false) }
    val angebotKannAngenommenWerden = selectedCr.zustand == ZustandManager.CR.ANGEBOT_UNTERBREITET
    val angebotIstBeauftragt = selectedCr.zustand >= ZustandManager.CR.VERBINDLICH
    val kundeKannAbnehmen = selectedCr.zustand == ZustandManager.CR.IN_TEST

    Div({
        style {
            flexGrow(1)
            width(100.percent)
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            gap(18.px)
            padding(24.px, 0.px, 24.px, 0.px)
            property("box-sizing", "border-box")
        }
    }) {
        Div({
            style {
                display(DisplayStyle.Flex)
                flexWrap(FlexWrap.Wrap)
                alignItems(AlignItems.Stretch)
                gap(12.px)
                width(100.percent)
                maxWidth(CUS_CR_DETAIL_MAX_WIDTH.px)
                alignSelf(AlignSelf.Center)
            }
        }) {
            CRInfoBlock("Ticketnummer", selectedCr.crNummer.ifBlank { "CR-${selectedCr.id}" })
            CRInfoBlock("Firma", selectedCr.firma.ifBlank { selectedCr.kunde.ifBlank { "-" } })
            CRInfoBlock("FirmenId", selectedCr.firmenId.ifBlank { "-" })
            CRInfoBlock("Zustand", ZustandManager.CR.textFor(selectedCr.zustand))
            CRInfoBlock("CR-Art", if (selectedCr.crNeuesModul) "Neues Modul" else "Änderung")
            CRInfoBlock("Betreff", selectedCr.crBeschreibung.ifBlank { "-" })
            CRInfoBlock("Aufwand (geschaetzt)", if (selectedCr.crAufwandStunden > 0) "${selectedCr.crAufwandStunden} Stunden" else "-")
            CRInfoBlock("Aufwand (final)", if (selectedCr.crAufwandFinal > 0) "${selectedCr.crAufwandFinal} Stunden" else "-")
        }
        Div({
            style {
                width(100.percent)
                maxWidth(CUS_CR_DETAIL_MAX_WIDTH.px)
                alignSelf(AlignSelf.Center)
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                gap(8.px)
            }
        }) {
            Div({
                style {
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    gap(10.px)
                    width(100.percent)
                }
            }) {
                Span({
                    style {
                        color(Color.white)
                        cursor("pointer")
                        fontSize(16.px)
                        width(18.px)
                        textAlign("center")
                        property("user-select", "none")
                    }
                    onClick { showAnforderung = !showAnforderung }
                }) {
                    Text(if (showAnforderung) "▾" else "▸")
                }
                Span({ style { color(DashboardTheme.TEXT_MUTED); fontSize(12.px); property("text-transform", "uppercase"); letterSpacing(0.6.px) } }) {
                    Text("Anforderungen")
                }
            }
            if (showAnforderung) {
                CRDetailField("Anforderungen", selectedCr.crAnforderung.ifBlank { "-" }, preWrap = true)
            }
            Div({ style { width(100.percent); height(1.px); backgroundColor(Color("rgba(255,255,255,0.18)")) } }) {}
            Div({
                style {
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    gap(10.px)
                    width(100.percent)
                }
            }) {
                Span({
                    style {
                        color(Color.white)
                        cursor("pointer")
                        fontSize(16.px)
                        width(18.px)
                        textAlign("center")
                        property("user-select", "none")
                    }
                    onClick { showCrInhalt = !showCrInhalt }
                }) {
                    Text(if (showCrInhalt) "▾" else "▸")
                }
                Span({ style { color(DashboardTheme.TEXT_MUTED); fontSize(12.px); property("text-transform", "uppercase"); letterSpacing(0.6.px) } }) {
                    Text("CR Inhalt")
                }
            }
            if (showCrInhalt) {
                CRDetailField("CR Inhalt", selectedCr.crInhalt.ifBlank { "-" }, preWrap = true)
            }
        }
        Div({
            style {
                width(100.percent)
                maxWidth(CUS_CR_DETAIL_MAX_WIDTH.px)
                alignSelf(AlignSelf.Center)
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.Center)
                gap(14.px)
            }
        }) {
            if (selectedCr.zustand >= ZustandManager.CR.ANGEBOT_UNTERBREITET) {
                Button(attrs = {
                    style {
                        height(44.px)
                        padding(0.px, 16.px)
                        borderRadius(8.px)
                        backgroundColor(Color.transparent)
                        color(Color.white)
                        property("border", "1px solid rgba(255,255,255,0.34)")
                        cursor("pointer")
                        display(DisplayStyle.Flex)
                        alignItems(AlignItems.Center)
                        justifyContent(JustifyContent.Center)
                        gap(8.px)
                    }
                    onClick { onOpenDocument() }
                }) {
                    Img(src = "images/view.svg", attrs = {
                        style {
                            width(20.px)
                            height(20.px)
                            property("filter", "brightness(0) invert(1)")
                        }
                    })
                    Span({ style { fontSize(14.px); fontWeight("600") } }) {
                        Text(if (angebotIstBeauftragt) "CR anzeigen" else "Angebot anzeigen")
                    }
                }
                if (angebotKannAngenommenWerden || kundeKannAbnehmen) {
                    Button(attrs = {
                        style {
                            height(44.px)
                            padding(0.px, 16.px)
                            borderRadius(8.px)
                            backgroundColor(Color.transparent)
                            color(Color.white)
                            property("border", "1px solid rgba(255,255,255,0.34)")
                            cursor(
                                when {
                                    angebotKannAngenommenWerden && !accepting -> "pointer"
                                    kundeKannAbnehmen && !confirmingReadyForDeployment -> "pointer"
                                    else -> "default"
                                }
                            )
                            display(DisplayStyle.Flex)
                            alignItems(AlignItems.Center)
                            justifyContent(JustifyContent.Center)
                            gap(8.px)
                            opacity(
                                when {
                                    angebotKannAngenommenWerden -> 1.0
                                    kundeKannAbnehmen -> 1.0
                                    else -> 0.62
                                }
                            )
                        }
                        if ((angebotKannAngenommenWerden && accepting) || (kundeKannAbnehmen && confirmingReadyForDeployment)) disabled()
                        onClick {
                            when {
                                angebotKannAngenommenWerden -> onAcceptOffer()
                                kundeKannAbnehmen -> onConfirmReadyForDeployment()
                            }
                        }
                    }) {
                        Span({ style { fontSize(14.px); fontWeight("600") } }) {
                            Text(
                                when {
                                    angebotKannAngenommenWerden && accepting -> "..."
                                    angebotKannAngenommenWerden -> "Angebot annehmen"
                                    kundeKannAbnehmen && confirmingReadyForDeployment -> "..."
                                    else -> "CR Abgenommen"
                                }
                            )
                        }
                    }
                }
            }
            Button(attrs = {
                style {
                    width(44.px)
                    height(44.px)
                    padding(0.px)
                    borderRadius(8.px)
                    backgroundColor(Color.transparent)
                    color(Color.white)
                    property("border", "1px solid rgba(255,255,255,0.34)")
                    cursor("pointer")
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    justifyContent(JustifyContent.Center)
                }
                onClick { onOpenChat() }
            }) {
                Img(src = "images/chat.svg", attrs = {
                    style {
                        width(20.px)
                        height(20.px)
                        property("filter", "brightness(0) invert(1)")
                    }
                })
            }
        }
    }
}

@Composable
internal fun CUSCRPreviewDocument(
    selectedPreviewLayout: CrPreviewLayout?,
    previewOrtDatumText: String,
    previewSignaturName: String,
    previewSignaturRole: String,
    previewFirmenName: String,
    previewSignaturBild: String,
    rootElementId: String? = null
) {
    if (selectedPreviewLayout != null) {
        CRLayoutSys(
            rootElementId = rootElementId,
            eigeneDatenZeile = selectedPreviewLayout.eigeneDatenZeile,
            empfaengerFirmenname = selectedPreviewLayout.empfaengerFirmenname,
            empfaengerStrasseHausnummer = selectedPreviewLayout.empfaengerStrasseHausnummer,
            empfaengerPlzOrt = selectedPreviewLayout.empfaengerPlzOrt,
            firmenIdText = selectedPreviewLayout.firmenIdText,
            absenderFirmenName = selectedPreviewLayout.absenderFirmenName,
            absenderStrasseHausnummer = selectedPreviewLayout.absenderStrasseHausnummer,
            absenderPlzOrt = selectedPreviewLayout.absenderPlzOrt,
            absenderDatum = selectedPreviewLayout.absenderDatum,
            betreffText = selectedPreviewLayout.betreffText,
            briefText = selectedPreviewLayout.briefText,
            headerMode = "LOGO",
            footerMode = "BUSINESS",
            grussformelText = selectedPreviewLayout.grussformelText,
            ortDatumText = selectedPreviewLayout.ortDatumText,
            logoSrc = selectedPreviewLayout.logoSrc,
            footerLines = selectedPreviewLayout.footerLines,
            signaturBildSrc = selectedPreviewLayout.signaturBildSrc,
            signaturNameText = selectedPreviewLayout.signaturNameText,
            secondGrussformelText = "",
            secondOrtDatumText = previewOrtDatumText,
            secondSignaturBildSrc = previewSignaturBild,
            secondSignaturNameText = listOf(
                previewSignaturName,
                listOf(previewSignaturRole, previewFirmenName).filter { it.isNotBlank() }.joinToString(", ")
            ).filter { it.isNotBlank() }.joinToString("\n"),
            editable = false,
            onEmpfaengerFirmennameChange = {},
            onEmpfaengerStrasseHausnummerChange = {},
            onEmpfaengerPlzOrtChange = {},
            onFirmenIdTextChange = {},
            onAbsenderFirmenNameChange = {},
            onAbsenderStrasseHausnummerChange = {},
            onAbsenderPlzOrtChange = {},
            onAbsenderDatumChange = {},
            onBetreffChange = {},
            onTextChange = {},
            onGrussformelChange = {},
            onSignaturOrtDatumChange = {},
            onSignaturNameChange = {},
            onFooterLineChange = { _, _ -> }
        )
    }
}

@Composable
internal fun CUSCRBearbeitenPreviewOverlay(
    selectedPreviewLayout: CrPreviewLayout?,
    previewOrtDatumText: String,
    previewSignaturName: String,
    previewSignaturRole: String,
    previewFirmenName: String,
    previewSignaturBild: String,
    onShowPdfPreviewChange: (Boolean) -> Unit
) {
    Div({
        style {
            position(Position.Fixed)
            left(0.px)
            top(0.px)
            right(0.px)
            bottom(0.px)
            backgroundColor(Color("rgba(8,12,18,0.72)"))
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            justifyContent(JustifyContent.Center)
            padding(24.px)
            property("box-sizing", "border-box")
            property("z-index", "5000")
        }
        onClick { onShowPdfPreviewChange(false) }
    }) {
        Div({
            style {
                position(Position.Relative)
                width(100.percent)
                maxWidth(920.px)
                maxHeight(100.percent)
                overflowY("auto")
            }
            onClick { it.stopPropagation() }
        }) {
            Div({
                style {
                    display(DisplayStyle.Flex)
                    justifyContent(JustifyContent.FlexEnd)
                    paddingBottom(12.px)
                }
            }) {
                Button(attrs = {
                    style {
                        width(44.px)
                        height(44.px)
                        padding(0.px)
                        borderRadius(8.px)
                        backgroundColor(Color.transparent)
                        color(Color.white)
                        property("border", "1px solid rgba(255,255,255,0.34)")
                        cursor("pointer")
                        display(DisplayStyle.Flex)
                        alignItems(AlignItems.Center)
                        justifyContent(JustifyContent.Center)
                    }
                    onClick { onShowPdfPreviewChange(false) }
                }) { Text("X") }
            }
            if (selectedPreviewLayout != null) {
                CUSCRPreviewDocument(
                    selectedPreviewLayout = selectedPreviewLayout,
                    previewOrtDatumText = previewOrtDatumText,
                    previewSignaturName = previewSignaturName,
                    previewSignaturRole = previewSignaturRole,
                    previewFirmenName = previewFirmenName,
                    previewSignaturBild = previewSignaturBild
                )
            } else {
                Div({
                    style {
                        width(100.percent)
                        padding(24.px)
                        borderRadius(12.px)
                        backgroundColor(Color.white)
                        color(Color("#1d2733"))
                        textAlign("center")
                        property("box-sizing", "border-box")
                    }
                }) { Text("Keine Angebotsvorschau vorhanden.") }
            }
        }
    }
}

@Composable
internal fun CRDetailField(
    label: String,
    value: String,
    preWrap: Boolean = false,
    compact: Boolean = false
) {
    Div({
        style {
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            gap(if (compact) 4.px else 6.px)
            if (compact) {
                property("width", "fit-content")
                property("flex", "0 1 220px")
                minWidth(160.px)
                maxWidth(320.px)
            } else {
                width(100.percent)
            }
            padding(if (compact) 10.px else 14.px)
            borderRadius(12.px)
            backgroundColor(DashboardTheme.SURFACE_CARD)
            property("border", "1px solid rgba(255,255,255,0.10)")
            property("box-sizing", "border-box")
            minWidth(0.px)
        }
    }) {
        Span({
            style {
                color(DashboardTheme.TEXT_MUTED)
                fontSize(if (compact) 10.px else 12.px)
                property("text-transform", "uppercase")
                letterSpacing(if (compact) 0.4.px else 0.6.px)
            }
        }) { Text(label) }
        Span({
            style {
                fontWeight("600")
                color(DashboardTheme.TEXT_LIGHT)
                fontSize(if (compact) 13.px else 15.px)
                property("overflow", "hidden")
                property("text-overflow", "ellipsis")
                property("white-space", if (preWrap) "pre-wrap" else "nowrap")
                if (preWrap) property("white-space", "pre-wrap")
            }
        }) { Text(value) }
    }
}

@Composable
internal fun CRInfoBlock(label: String, value: String) {
    CRDetailField(label, value, compact = true)
}
